import { Item, Ordered, OrderedTitle, Td, Th } from "./helperComponents";
import PolicyContent, { ContentItem } from "./PolicyContent";

const italicSite = <i>site</i>;
const italicCookies = <i>cookies</i>;

export enum Browsers {
	FIREFOX = "FIREFOX",
	CHROME = "CHROME",
	EDGE = "EDGE",
	SAFARI = "SAFARI",
	OPERA = "OPERA"
}

const browserPrivacyAndCookieLinks: Record<Browsers, string> = {
	[Browsers.FIREFOX]:
		"https://support.mozilla.org/pt-BR/kb/protecao-aprimorada-contra-rastreamento-firefox-desktop?redirectslug=enable-and-disable-cookies-website-preferences",
	[Browsers.CHROME]: "https://support.google.com/chrome/answer/95647?hl=pt-BR",
	[Browsers.EDGE]:
		"https://support.microsoft.com/pt-br/windows/gerenciar-cookies-no-microsoft-edge-exibir-permitir-bloquear-excluir-e-usar-168dab11-0753-043d-7c16-ede5947fc64d",
	[Browsers.SAFARI]: "https://support.apple.com/pt-br/guide/safari/sfri11471/mac",
	[Browsers.OPERA]: "https://help.opera.com/en/latest/security-and-privacy/#privateWindow"
};

const browserNames: Record<Browsers, string> = {
	[Browsers.FIREFOX]: "Firefox",
	[Browsers.CHROME]: "Chrome",
	[Browsers.EDGE]: "Internet Explorer",
	[Browsers.SAFARI]: "Safari",
	[Browsers.OPERA]: "Opera"
};

const browserPrivateWindowNames: Record<Browsers, string> = {
	[Browsers.FIREFOX]: "Navegação Privada",
	[Browsers.CHROME]: "Navegação como Incógnito",
	[Browsers.EDGE]: "Navegação Privada",
	[Browsers.SAFARI]: "Navegação Privada",
	[Browsers.OPERA]: "Navegação Privada"
};

const browserCookieConfigItem = (browser: Browsers, idx: number): JSX.Element => (
	<Item key={browserPrivateWindowNames[browser] + idx}>
		Para mais informações sobre “{browserPrivateWindowNames[browser]}” e gestão de {italicCookies} no <i>browser</i> {browserNames[browser]},{" "}
		<a href={browserPrivacyAndCookieLinks[browser]} target="_blank" rel="noreferrer">
			clique aqui
		</a>
		;
	</Item>
);

const cookiePolicyStructure: ContentItem[] = [
	{
		title: <OrderedTitle marker="1.">Introdução</OrderedTitle>,
		content: (
			<>
				<p>
					O https://isentos.motivapagamentos.com.br utiliza {italicCookies} para melhorar o desempenho e a sua experiência ao utilizar nosso{" "}
					{italicSite}.
				</p>
				<p>Nós procuramos, assim, explicar de forma transparente como, quando e porque utilizamos {italicCookies}.</p>
				<p>Ao acessar o nosso {italicSite}, você autoriza o uso cookies nos termos desta Política.</p>
				<p>
					Caso não concorde com o uso de {italicCookies} dessa forma, você poderá: (i) ajustar as configurações de seu navegador para não
					permitir o uso de {italicCookies} ou (ii) não acessar o nosso {italicSite}.
				</p>
				<p>
					Lembramos que desabilitar o uso de {italicCookies} poderá impactar na sua experiência ao navegar em nosso {italicSite}.
				</p>
				<p>
					Para quaisquer questões ou dúvidas sobre esta Política de <i>Cookies</i>, você poderá entrar em contato conosco por meio do
					endereço eletrônico: encarregado.dadospessoais@motiva.com.br.
				</p>
			</>
		)
	},
	{
		title: <OrderedTitle marker="2.">O que são {italicCookies}?</OrderedTitle>,
		content: (
			<p>
				<i>Cookies</i> são arquivos digitais com pequenos fragmentos de dados (e geralmente com um identificador único), que são armazenados
				em seu dispositivo por meio do navegador ou aplicativo e que guardam informações relacionadas com as suas preferências.
			</p>
		)
	},
	{
		title: <OrderedTitle marker="3.">Para que servem os {italicCookies}?</OrderedTitle>,
		content: (
			<>
				<p>
					Os {italicCookies} servem para aprimorar a sua experiência, tanto em termos de performance como em termos de usabilidade, uma vez
					que os conteúdos disponibilizados serão direcionados às suas necessidades e expectativas.
				</p>
				<p>
					Os {italicCookies} permitem que nosso {italicSite} memorize informações sobre a sua visita, o seu idioma preferido, a sua
					localização, a recorrência das suas sessões e outras variáveis que nós consideramos relevantes para tornar sua experiência muito
					mais eficiente.
				</p>
				<p>
					Os {italicCookies} também poderão ser utilizados para compilar <span className="underline">estatísticas anônimas</span> e
					agregadas que permitem entender como os usuários utilizam e interagem com nosso {italicSite}, bem como para aprimorar suas
					estruturas e conteúdo. Por serem estatísticas anônimas,{" "}
					<span className="underline font-semibold">não podemos identificá-lo pessoalmente por meio desses dados</span>.
				</p>
				<p>
					A utilização de {italicCookies} é algo comum em qualquer {italicSite} atualmente. O seu uso não prejudica de forma alguma os
					dispositivos (computadores, <i>smartphones</i>, <i>tablets</i>, etc.) em que são armazenados.
				</p>
			</>
		)
	},
	{
		title: <OrderedTitle marker="4.">Que tipo de {italicCookies} utilizamos?</OrderedTitle>,
		content: (
			<>
				<p>
					Nós utilizamos dois tipos de {italicCookies} em nosso {italicSite}: {italicCookies} de funcionalidades e {italicCookies} de sessão
				</p>
				<ul>
					<Item leadingText="Cookies de funcionalidades:">
						são utilizados para assegurar a disponibilização de funcionalidades adicionais no {italicSite} ou para guardar as preferências
						definidas pelo usuário, sempre que utilizar o mesmo dispositivo.
					</Item>
					<Item leadingText="Cookies de sessão:">
						são os {italicCookies} temporários que permanecem arquivados até que o usuário saia do {italicSite} ou encerre o navegador.
					</Item>
				</ul>
			</>
		)
	},
	{
		title: <OrderedTitle marker="4.">É possível controlar ou excluir {italicCookies}?</OrderedTitle>,
		content: (
			<>
				<p>A maioria dos navegadores de Internet é configurada para aceitar automaticamente os {italicCookies}.</p>
				<p>
					Você pode alterar as configurações para bloquear o uso de {italicCookies} ou alertá-lo quando um <i>cookie</i> estiver sendo
					enviado para seu dispositivo. Existem várias formas de gerenciar {italicCookies}.
				</p>
				<p>
					Ao desabilitar os {italicCookies}, você talvez não consiga visitar certas áreas de uma página nossa ou talvez não receba
					informações personalizadas quando visitar uma página.
				</p>
				<p>
					Caso você utilize dispositivos diferentes para acessar o nosso {italicSite} (computador, <i>smartphone</i>, <i>tablet</i>, etc.),
					deverá assegurar-se de que cada navegador de cada dispositivo esteja ajustado para atender às suas preferências em relação aos{" "}
					{italicCookies}.
				</p>
				<p>
					Para que você possa, de forma simples e intuitiva, gerir suas preferências quanto aos {italicCookies} a partir do seu navegador,
					poderá utilizar um dos links abaixo:
				</p>
				<ul>{Object.values(Browsers).map((browser, idx) => browserCookieConfigItem(browser, idx))}</ul>
			</>
		)
	},
	{
		title: <OrderedTitle marker="6.">Informações detalhadas sobre os {italicCookies}</OrderedTitle>,
		content: (
			<div>
				<p>
					Apresentamos abaixo os detalhes sobre o uso dos {italicCookies} em nosso site, bem como as finalidades de tratamento dos dados que
					os justificam.
				</p>
				<Ordered>
					<Item marker="6.1." leadingText="Cookies de Funcionalidade">
						{" "}
					</Item>
				</Ordered>
				<table>
					<thead className="relative">
						<tr>
							<Th>Nome do Cookie</Th>
							<Th>Provedor</Th>
							<Th>Validade</Th>
							<Th>Finalidade</Th>
						</tr>
					</thead>
					<tbody>
						<tr className="text-center">
							<Td>_ga</Td>
							<Td>ccrrodovias</Td>
							<Td>2 anos</Td>
							<Td>Usado para distinguir usuários</Td>
						</tr>
						<tr className="text-center">
							<Td>{"_ga_<container-id>"}</Td>
							<Td>ccrrodovias</Td>
							<Td>2 anos</Td>
							<Td>Usado para manter o estado da sessão</Td>
						</tr>
					</tbody>
				</table>
			</div>
		)
	},
	{
		title: "",
		content: (
			<>
				<br />
				<br />
				<p>Atualização: outubro de 2025</p>
			</>
		)
	}
];

export default function CookiePolicy(): JSX.Element {
	return <PolicyContent content={cookiePolicyStructure} />;
}
